import { useFormatting } from "@msys/ui";
import { Functions as FunctionsIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import type {
  GridRenderCellParams,
  GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import React from "react";
import { AttributeExpressionModal } from "../../doc-items/modals/AttributeExpressionModal.js";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { DocDataGridRow } from "../types.js";
import { DataGridFloatEditCell } from "./DataGridFloatEditCell.js";

interface Expression {
  attribute: string;
  expr: string;
  result: number | { __type: "missing-value" };
  missingValue: boolean;
}

interface DisplayProps<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  params: GridRenderCellParams<DocDataGridRow<T>>;
  fieldName: string;
  projectId: string | null;
  docId: string;
  formattedValue: string;
}

/** Read-only display — shows expression button or formatted value */
export const DataGridFloatWithExpressionDisplay = <
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>({
  params,
  fieldName,
  projectId,
  docId,
  formattedValue,
}: DisplayProps<T>) => {
  const { getFormattedFloat } = useFormatting();
  const [isExpressionModalOpen, setExpressionModalOpen] = React.useState(false);

  const item = params.row.item as EstimatedCalculation__ItemFragment | null;
  const expression: Expression | undefined = item?.attributeExpressions?.find(
    e => e.attribute === fieldName
  ) as Expression | undefined;

  const hasExpressionRefError =
    expression &&
    typeof expression.result !== "number" &&
    (expression.result as any)?.__type === "missing-value";

  if (!expression) {
    return <span>{formattedValue}</span>;
  }

  return (
    <>
      <IconButton
        size="small"
        color="secondary"
        onClick={e => {
          e.stopPropagation();
          setExpressionModalOpen(true);
        }}
      >
        <FunctionsIcon sx={{ fontSize: "1rem" }} />
      </IconButton>
      {hasExpressionRefError ? (
        <span style={{ color: "red" }}>{"Ref error"}</span>
      ) : (
        <span>{formattedValue}</span>
      )}
      {isExpressionModalOpen && item && (
        <AttributeExpressionModal
          projectId={projectId}
          docId={docId}
          itemId={item.id}
          fieldName={fieldName}
          expression={expression?.expr}
          handleClose={() => setExpressionModalOpen(false)}
        />
      )}
    </>
  );
};

interface EditProps extends GridRenderEditCellParams {
  min?: number;
  max?: number;
  multiplier?: number;
  unit?: string;
}

/** Edit cell — delegates to DataGridFloatEditCell (only shown when no expression) */
export const DataGridFloatWithExpressionEditCell = (props: EditProps) => {
  return <DataGridFloatEditCell {...props} />;
};
